import {
  userErrorMessage,
  questionBankCatalogResponseSchema,
  questionBankMutationResponseSchema,
  questionBankProblemsResponseSchema,
  type QuestionBankCatalogResponse,
  type QuestionBankProblem,
  type QuestionBankSection,
  type QuestionRequirementsRichText,
} from '@wx-weiqi/contracts';

import { nextRequestId } from '../services/http';
import type { IdentityStore } from './identity-store';

type Category = QuestionBankCatalogResponse['data']['categories'][number];

export const QUESTION_BANK_PAGE_SIZE = 8;

export interface QuestionBankState {
  readonly categories: readonly Category[];
  readonly selectedKnowledgePointId: string | null;
  readonly problems: readonly QuestionBankProblem[];
  readonly totalProblems: number;
  readonly problemPage: number;
  readonly problemPageCount: number;
  readonly pending: string | null;
  readonly error: string | null;
  readonly notice: string | null;
}

type Listener = () => void;
type CatalogRefreshOptions = Readonly<{ preserveProblemPage?: boolean }>;

export class QuestionBankStore {
  private stateValue: QuestionBankState = {
    categories: [],
    selectedKnowledgePointId: null,
    problems: [],
    totalProblems: 0,
    problemPage: 1,
    problemPageCount: 1,
    pending: null,
    error: null,
    notice: null,
  };
  private readonly listeners = new Set<Listener>();

  constructor(private readonly identity: IdentityStore) {}

  readonly subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly snapshot = (): QuestionBankState => this.stateValue;

  clear(): void {
    this.update({
      categories: [],
      selectedKnowledgePointId: null,
      problems: [],
      totalProblems: 0,
      problemPage: 1,
      problemPageCount: 1,
      pending: null,
      error: null,
      notice: null,
    });
  }

  clearMessages(): void {
    this.update({ ...this.stateValue, error: null, notice: null });
  }

  async loadCatalog(options: CatalogRefreshOptions = {}): Promise<void> {
    this.pending('load-catalog');
    try {
      const response = await this.identity.authorized(
        '/question-bank/catalog',
        questionBankCatalogResponseSchema,
      );
      const availableIds = new Set(
        response.data.categories.flatMap((category) =>
          category.knowledgePoints.map((point) => point.knowledgePointId),
        ),
      );
      const selected =
        this.stateValue.selectedKnowledgePointId !== null &&
        availableIds.has(this.stateValue.selectedKnowledgePointId)
          ? this.stateValue.selectedKnowledgePointId
          : (response.data.categories.flatMap((category) => category.knowledgePoints)[0]
              ?.knowledgePointId ?? null);
      const selectedPoint = response.data.categories
        .flatMap((category) => category.knowledgePoints)
        .find((point) => point.knowledgePointId === selected);
      // Editing changes content, not list position. Reset only for a different directory
      // or when the current page no longer exists after concurrent deletions.
      const page =
        options.preserveProblemPage && selected === this.stateValue.selectedKnowledgePointId
          ? Math.min(
              this.stateValue.problemPage,
              Math.max(1, Math.ceil((selectedPoint?.problemCount ?? 0) / QUESTION_BANK_PAGE_SIZE)),
            )
          : 1;
      this.update({
        ...this.stateValue,
        categories: response.data.categories,
        selectedKnowledgePointId: selected,
        pending: null,
        error: null,
      });
      if (selected !== null) await this.selectKnowledgePoint(selected, page);
      else {
        this.update({
          ...this.stateValue,
          problems: [],
          totalProblems: 0,
          problemPage: 1,
          problemPageCount: 1,
        });
      }
    } catch (error: unknown) {
      this.fail(error, '题库加载失败，请重试。');
      throw error;
    }
  }

  async selectKnowledgePoint(knowledgePointId: string, page = 1): Promise<void> {
    this.update({ ...this.stateValue, selectedKnowledgePointId: knowledgePointId });
    this.pending('load-problems');
    try {
      const query = new URLSearchParams({
        knowledgePointId,
        page: String(page),
        pageSize: String(QUESTION_BANK_PAGE_SIZE),
      });
      const response = await this.identity.authorized(
        `/question-bank/problems?${query.toString()}`,
        questionBankProblemsResponseSchema,
      );
      if (this.stateValue.selectedKnowledgePointId !== knowledgePointId) return;
      this.update({
        ...this.stateValue,
        problems: response.data.problems,
        totalProblems: response.data.total,
        problemPage: response.data.page,
        problemPageCount: response.data.pageCount,
        pending: null,
        error: null,
      });
    } catch (error: unknown) {
      this.fail(error, '题目列表加载失败，请重试。');
      throw error;
    }
  }

  async createCategory(section: QuestionBankSection, name: string): Promise<void> {
    await this.mutate(
      'create-category',
      '/question-bank/categories',
      'POST',
      { section, name },
      '分类已创建。',
    );
  }

  async updateCategory(category: Category, name: string): Promise<void> {
    await this.mutate(
      'update-category',
      `/question-bank/categories/${encodeURIComponent(category.categoryId)}`,
      'PATCH',
      { expectedVersion: category.version, name },
      '分类名称已更新。',
    );
  }

  async deleteCategory(category: Category): Promise<void> {
    await this.mutate(
      'delete-category',
      `/question-bank/categories/${encodeURIComponent(category.categoryId)}`,
      'DELETE',
      { expectedVersion: category.version },
      '分类已删除。',
    );
  }

  async createKnowledgePoint(categoryId: string, name: string): Promise<void> {
    await this.mutate(
      'create-knowledge-point',
      `/question-bank/categories/${encodeURIComponent(categoryId)}/knowledge-points`,
      'POST',
      { name },
      '知识点目录已创建。',
    );
  }

  async updateKnowledgePoint(
    point: Category['knowledgePoints'][number],
    name: string,
  ): Promise<void> {
    await this.mutate(
      'update-knowledge-point',
      `/question-bank/knowledge-points/${encodeURIComponent(point.knowledgePointId)}`,
      'PATCH',
      { expectedVersion: point.version, name },
      '知识点名称已更新。',
    );
  }

  async deleteKnowledgePoint(point: Category['knowledgePoints'][number]): Promise<void> {
    await this.mutate(
      'delete-knowledge-point',
      `/question-bank/knowledge-points/${encodeURIComponent(point.knowledgePointId)}`,
      'DELETE',
      { expectedVersion: point.version },
      '知识点目录已删除。',
    );
  }

  async sortProblems(): Promise<void> {
    if (this.stateValue.pending !== null) return;
    const point = this.stateValue.categories
      .flatMap((category) => category.knowledgePoints)
      .find((candidate) => candidate.knowledgePointId === this.stateValue.selectedKnowledgePointId);
    if (point === undefined || this.stateValue.totalProblems < 2) return;
    const notice = '已按日期从早到晚、同日题号从小到大排序并保存。';
    await this.mutate(
      'sort-problems',
      `/question-bank/knowledge-points/${encodeURIComponent(point.knowledgePointId)}/problems/auto-sort`,
      'POST',
      { expectedVersion: point.version },
      notice,
    );
    this.update({ ...this.stateValue, notice });
  }

  async importProblems(file: File): Promise<void> {
    const knowledgePointId = this.stateValue.selectedKnowledgePointId;
    if (knowledgePointId === null) throw new TypeError('请先选择知识点目录。');
    this.pending('import-problems');
    try {
      const formData = new FormData();
      formData.set('file', file, file.name);
      const response = await this.identity.authorized(
        `/question-bank/knowledge-points/${encodeURIComponent(knowledgePointId)}/problems/import`,
        questionBankMutationResponseSchema,
        {
          method: 'POST',
          requestId: nextRequestId('web-question-import'),
          formData,
        },
      );
      this.update({
        ...this.stateValue,
        pending: null,
        error: null,
        notice: `已成功导入 ${response.data.importedCount ?? 0} 道题目。`,
      });
      await this.loadCatalog();
    } catch (error: unknown) {
      this.fail(error, '题目导入失败。');
      throw error;
    }
  }

  async deleteProblem(problem: QuestionBankProblem): Promise<void> {
    await this.mutate(
      'delete-problem',
      `/question-bank/problems/${encodeURIComponent(problem.problemId)}`,
      'DELETE',
      { expectedVersion: problem.version },
      '题目已删除。',
    );
  }

  async updateProblemRequirements(
    problem: QuestionBankProblem,
    requirements: string,
    requirementsRichText?: QuestionRequirementsRichText | null,
  ): Promise<void> {
    await this.mutate(
      'edit-problem',
      `/question-bank/problems/${encodeURIComponent(problem.problemId)}`,
      'PATCH',
      {
        expectedVersion: problem.version,
        requirements: requirements.trim(),
        ...(requirementsRichText === undefined ? {} : { requirementsRichText }),
      },
      '题目要求已保存。',
      { preserveProblemPage: true },
    );
  }

  private async mutate(
    pending: string,
    path: string,
    method: 'POST' | 'PATCH' | 'DELETE',
    body: unknown,
    notice: string,
    refreshOptions: CatalogRefreshOptions = {},
  ): Promise<void> {
    this.pending(pending);
    try {
      await this.identity.authorized(path, questionBankMutationResponseSchema, {
        method,
        requestId: nextRequestId(`web-${pending}`),
        body,
      });
      await this.loadCatalog(refreshOptions);
      this.update({ ...this.stateValue, pending: null, error: null, notice });
    } catch (error: unknown) {
      this.fail(error, '题库操作失败，请重试。');
      throw error;
    }
  }

  private pending(pending: string): void {
    this.update({ ...this.stateValue, pending, error: null, notice: null });
  }

  private fail(error: unknown, fallback: string): void {
    this.update({
      ...this.stateValue,
      pending: null,
      error: userErrorMessage(error, fallback),
      notice: null,
    });
  }

  private update(state: QuestionBankState): void {
    this.stateValue = state;
    this.listeners.forEach((listener) => listener());
  }
}
